import ChevronIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { Case } from "../../shared/CaseModel";
import {
  outstandingOf,
  PartyReserve,
  partyReserveOf,
  RESERVE_HEADS,
  ReserveHeads,
  reserveTotal,
} from "../../shared/reserve";
import { ProgressBar } from "../CostProgressBar";
import { Metric } from "../general/DataAtoms";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";

const card = { borderRadius: 3 } as const;

export type { PartyReserve };

/** The reserve of a party of a case (see partyReserveOf). */
export const casePartyReserveOf = (c: Case, partyId: string) =>
  partyReserveOf(c.results, partyId);

/** The reserve in a figure, with how much of it is paid. */
export const ReserveCard = ({ reserve }: { reserve?: PartyReserve }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve" });
  return (
    <Paper variant="outlined" sx={{ ...card, px: 2.5, py: 2, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <SectionLabel component="h2">{t("title")}</SectionLabel>
        <Link
          component={RouterLink}
          to="analytics/reserve"
          variant="body2"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", fontWeight: 600 }}
        >
          {t("title")}
          <ChevronIcon fontSize="small" />
        </Link>
      </Stack>
      {reserve ? (
        <>
          <Metric
            label={t("indicated")}
            value={reserveTotal(reserve.total)}
            caption={t("caption")}
            sx={{ mt: 1.75 }}
          />
          <Box sx={{ mt: 1.5 }}>
            <ProgressBar
              incurred={reserveTotal(reserve.paid)}
              total={reserveTotal(reserve.total)}
            />
          </Box>
        </>
      ) : (
        <Typography variant="body2" sx={{ mt: 1.75, color: "text.secondary" }}>
          {t("unknown")}
        </Typography>
      )}
    </Paper>
  );
};

/** Each head of the reserve: what is paid of it, outstanding and incurred. */
export const ReserveSheet = ({
  reserve,
  name,
}: {
  reserve: PartyReserve;
  // the party, where the case has more than one whose reserve is shown
  name?: string;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "reserve.sheet" });
  const { t: tHeads } = useTranslation(undefined, {
    keyPrefix: "reserve.heads",
  });
  const { currency } = useCurrency();
  const outstanding = outstandingOf(reserve.total, reserve.paid);
  const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;
  // the figures of a row: paid, outstanding and incurred
  const columns = (pick: (heads: ReserveHeads) => number) =>
    [reserve.paid, outstanding, reserve.total].map((heads, index) => (
      <TableCell key={index} align="right" sx={amount}>
        {currency.format(pick(heads))}
      </TableCell>
    ));

  return (
    <Paper variant="outlined" sx={{ ...card, overflow: "hidden" }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <SectionLabel component="h2">
          {name ? t("titleOf", { name }) : t("title")}
        </SectionLabel>
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {t("description")}
        </Typography>
      </Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ "& td, & th": { px: 2.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t("head")}</TableCell>
              <TableCell align="right">{t("paid")}</TableCell>
              <TableCell align="right">{t("outstanding")}</TableCell>
              <TableCell align="right">{t("incurred")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {RESERVE_HEADS.map((head) => (
              <TableRow key={head}>
                <TableCell component="th" scope="row">
                  {tHeads(head)}
                </TableCell>
                {columns((heads) => heads[head])}
              </TableRow>
            ))}
            <TableRow sx={{ "& td, & th": { fontWeight: 700 } }}>
              <TableCell component="th" scope="row">
                {t("total")}
              </TableCell>
              {columns(reserveTotal)}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};
